/* =========================================
   SHARED SEO CONSTANTS
   Single source of truth for canonical URL, brand naming,
   and structured data. Keep brand strings consistent —
   Google builds entity recognition from repetition.
   ========================================= */

export const SITE_URL = 'https://payna.com'

export const BRAND = 'Payna'
export const LEGAL_NAME = 'Paena Technologies, Inc.'

/* Terms the licensing tables are published under. Google's Dataset
   docs want a URL that pins a specific version of a licence, so this
   points at the versioned CC deed rather than our terms page, which
   would move underneath the link. Reuse with attribution is the point:
   it is the same bet as llms.txt. */
export const DATA_LICENSE = 'https://creativecommons.org/licenses/by/4.0/'

export const SITE_NAME = 'Payna'

/* The homepage title is the one place the brand has to introduce
   itself. "Payna" alone competes with an unrelated Wikipedia entity,
   a game character, and a food brand for the bare word, and tells a
   searcher who does find it nothing about what this is. Keep the
   brand first (it is what people who already know us type) and the
   category immediately after.

   Shortened 2026-10-14 from "Payna | AI Licensing and Compliance for
   Regulated Companies" (56 chars), which rendered in full but spent
   most of its width on words that distinguish nothing: "Compliance
   for Regulated Companies" says the same thing twice, because a
   regulated company is the only kind that has compliance. 33 chars
   now, and the two words that were carrying their weight stayed.

   NAMES THE VERTICAL, ON PURPOSE (He Song, 2026-10-14). This is the
   highest-authority page on the domain, and it is now the single
   loudest statement of which market Payna sells to. It was briefly
   vertical-neutral ("Licensing for Regulated Finance") on the
   reasoning that one title has to cover insurance and lending too;
   overruled, and the mortgage commitment is the point.

   The cost is real and accepted: a searcher in insurance, lending or
   collections reads a homepage title with somebody else's vertical
   in it. Three things carry those readers instead, and all three are
   load-bearing now rather than nice to have — DEFAULT_DESCRIPTION
   below names NIPR in the next breath, the nav Industries menu still
   lists them, and each vertical has its own page with its own title
   (see INDUSTRIES in content/industries.ts). Widen this line again
   the moment mortgage stops being the only market worth naming. */
export const DEFAULT_TITLE = 'Payna | AI Licensing for Mortgage'

/* 318 characters until 2026-08-27, which is roughly double what a
   SERP will render, so most of it was never seen. It also read as a
   keyword inventory rather than a reason to click.

   What it says now is the thing competitors structurally do not do:
   license the COMPANY and the PEOPLE, on both registries. Brico
   covers entity licensing and states plainly that its mortgage
   product excludes individual MLO licensing; the insurance-native
   platforms do people but not the company. Both halves is the
   position, so both halves lead.

   "and the originators" (He Song, 2026-10-14) replaced "and the
   people who work for it", which was vague where it most needed to
   be sharp: it is the half competitors do not have, and it was
   spending nine words to say so without naming anyone. An originator
   is a specific licensed person a reader can picture, and it is the
   word that reader uses.

   ⚠ IT IS ALSO MORTGAGE-ONLY VOCABULARY, and the same clause names
   NIPR two breaths later. That is the deliberate consequence of the
   title above naming mortgage: an adjuster or producer reading this
   line sees somebody else's job title first. If insurance ever needs
   to lead again, this word and DEFAULT_TITLE move together or
   neither moves. */
export const DEFAULT_DESCRIPTION =
  'Payna licenses the company and the originators. ' +
  'Mortgage on NMLS, insurance on NIPR, applications through ' +
  'renewals, in all 50 states.'

/* Keywords are largely ignored by Google but still parsed by Bing,
   DuckDuckGo, and a number of AI crawlers. Ordered to match the site's
   positioning: brand, then mortgage and insurance, then lending and
   collections, then the payments verticals. */
export const KEYWORDS = [
  'Payna',
  'Payna compliance',
  'Payna licensing',
  'Paena Technologies',
  'mortgage licensing',
  'MLO licensing',
  'MU4 filing',
  'NMLS renewal automation',
  'NMLS sponsorship management',
  'mortgage company license',
  'insurance producer license',
  'insurance adjuster license',
  'NIPR licensing',
  'adjuster licensing software',
  'producer appointment management',
  'CE tracking',
  'lending license',
  'debt collection license',
  'collection agency license',
  'multi-state licensing',
  'state licensing compliance',
  'license renewal tracking',
  'surety bond compliance',
  'AI licensing agent',
  'licensing automation',
  'compliance automation software',
  'regulatory compliance platform',
  'license status API',
  'money transmitter license',
  'MTL licensing',
  'fintech licensing',
  'BSA AML compliance',
  'compliance operating system',
]

/* External profiles that corroborate the brand entity.
   VERIFY each URL resolves before shipping — a broken sameAs
   weakens the entity signal rather than strengthening it. */
export const SAME_AS = [
  /* Wikidata first. It is the only entry here that is an IDENTIFIER
     rather than a profile: Q141187417 is the node Google's Knowledge
     Graph reconciles against, and the item's own P856 points back at
     payna.com, so the claim is mutual and machine-checkable in both
     directions. That round trip is what promotes "payna" from a string
     Google spell-corrects to "patna" into a thing it recognises. */
  'https://www.wikidata.org/wiki/Q141187417',
  'https://www.ycombinator.com/companies/payna',
  'https://www.linkedin.com/company/payna',
  'https://x.com/payna_co',
  'https://www.crunchbase.com/organization/payna',
]
/* Checked 2026-08-25: the first three return 200 to an anonymous
   fetch. Crunchbase answers 403 to every non-browser client, so it
   cannot be checked that way; it ships on the owner's confirmation
   that the profile exists. A Cloudflare 403 is bot filtering, not a
   missing page, and Google crawls Crunchbase normally. PitchBook
   stays out until someone confirms that profile the same way.

   Wikidata Q141187417 created and verified 2026-08-27: label "Payna",
   description "American regulatory technology company", aliases Paena
   Technologies / Payna AI / Paena Technologies, Inc., and ten
   statements each carrying a reference. */

/* Complete per-page metadata in one call. Without this, subpages
   that set only title/description inherit the LAYOUT's openGraph
   and twitter blocks, so shared links preview as the homepage.
   A page-level openGraph REPLACES the root's resolved one
   (including the file-convention og:image), so the image must be
   restated here explicitly.

   ⚠ KEEP `title` UNDER ~270px RENDERED. NOT under a character count.
   A page title does two jobs, and the SHORTER budget is the one that
   bites: the page's own result gets the familiar full width, but a
   SITELINK under the brand query gets roughly a third of it, and
   Google cuts at a word boundary with an ellipsis rather than
   choosing different words.

   IT IS WIDTH, NOT CHARACTERS, and Google says so: "While there's no
   limit on how long a <title> element can be, the title link is
   truncated in Google Search results as needed, TYPICALLY TO FIT THE
   DEVICE WIDTH" (developers.google.com/search/docs/appearance/
   title-link). Google publishes no sitelink budget at all; the
   sitelinks page asks only that titles be "informative, relevant, and
   compact". Any number here is ours, not theirs.

   The band below IS ours, from the three sitelinks on the live `payna`
   result on 2026-10-14, each measured in Arial 20px:

     kept  "About Payna: Licensing for "       27ch  244.6px
     kept  "State License Applications for "   31ch  268.0px  <- widest kept
     kept  "License Renewal and "              20ch  196.8px
     cut    + "Regulated"                      36ch  335.8px
     cut    + "Regulated"                      40ch  359.1px
     cut    + "Compliance"                     30ch  302.4px  <- narrowest cut

   Threshold sits in (268px, 302px]. Budget 270px; treat over 290px as
   truncating.

   ⚠ DO NOT RESTATE THIS AS A CHARACTER RULE. It said "~32 characters"
   until 2026-10-15, and that cannot be right: in the same three
   observations 31ch SURVIVED and 30ch was CUT, so no character
   threshold fits. Proportional type is why, and it bites inside our
   own set too: "Collection Agency Licensing" is 27ch and 249.0px,
   while "MLO and Adjuster Licensing" is 26ch and 250.2px. More
   characters, narrower line. A capital M or W costs about double an i
   or an l. Measure the string, do not count it.

   The character rule got the right answer on all thirteen titles it
   fired on, because every original was 359px or wider. Its failure
   mode was OMISSION: /fintech (32ch, 292.4px) and /security (29ch,
   287.9px) both passed it and both sat above the widest title Google
   actually kept, and /fintech was one of the six sitelinks observed.
   Both are shortened now. /blog ("Licensing & Compliance Notes", 28ch,
   272.4px) is marginally over the 270 budget and well under the 290
   line; left alone deliberately, and the next person to touch it
   should measure rather than assume.

   That truncation is not a cosmetic problem. "License Renewal and ..."
   names no product and carries no keyword past the cut, so the slot is
   spent and says nothing.

   Short does not mean weak. The fix is not to drop keywords, it is to
   drop the words that were never working: "for Regulated Companies"
   qualifies nothing, because an unregulated company does not apply
   for a state license.

   ⚠ A COMPETITOR EXAMPLE USED TO SIT HERE AND IT WAS FABRICATED. It
   read: "Brico ranks 'Crypto Licensing Software' in 25 characters."
   Brico's homepage title is 92 characters
   ("Financial Licensing Software for Money Transmitters, Lenders, and
   Mortgage Providers | Brico", checked 2026-10-15), and the claim was
   never verified before being written down. It was the only piece of
   competitive evidence offered for this whole rule, so anyone who
   went looking would have found the proof did not exist and had no
   way to tell which of the rest still did.

   What IS measured is above: four of our own sitelinks, cut
   mid-phrase, on a live SERP screenshot dated 2026-10-14. That is the
   evidence for this rule. It does not need a competitor.

   The opposite lesson is also available from Brico and is worth more:
   58 of its 87 URLs are blog posts, and the pages that rank for its
   money terms are mostly those posts rather than its landing pages.
   Title length is a sitelink problem. Ranking is a content problem.
   Do not confuse the two.

   ⚠ AND THE TITLE MUST BE SUPPORTED BY THE PAGE. A title claiming a
   word the body never uses invites the rewrite this rule exists to
   prevent: Google replaces a title when it "detect[s] an issue on the
   page", drawing the replacement from on-page text and anchors. As of
   2026-10-15 four titles here say "Software" and the word appears
   ZERO times in the body of any of them (/insurance, /mortgage,
   /fintech, /maintain all say "platform" or "system"), and /crypto
   says "VASP" over a page that says "money transmission". Fixed below
   by naming the category in heroDesc. If you shorten a title to a
   keyword, make sure the page says it too.

   THE RULE APPLIES TO NAV-REACHABLE PAGES, not to everything. Google
   nominates sitelinks from pages it considers prominent, which in
   practice means the nav, the footer and the homepage. Two deep
   reference pages are deliberately left long, because the ~60
   characters is the budget that actually serves them:

     /licensing/mortgage              Mortgage Company Licensing by State  (35)
     /catastrophe-adjuster-licensing  Catastrophe Adjuster Licensing by State  (39)

   Both exist to rank for the long-tail query they spell out, and
   neither is a plausible brand sitelink. Cutting "by State" off them
   to satisfy a budget they are not spending would trade a real query
   for a hypothetical one. The blog is exempt for the same reason, so
   articleMetadata below is not held to this rule. */
const OG_IMAGE = {
  url: '/opengraph-image.png',
  width: 1200,
  height: 630,
  type: 'image/png',
  alt:
    'Payna: From the first license to the hundredth. One system for every ' +
    'application, renewal, and seat.',
}

export function pageMetadata({
  title,
  description,
  path,
  brandedTitle = false,
}: {
  title: string
  description: string
  path: string
  /* Set when `title` ALREADY CONTAINS THE BRAND, which suppresses the
     layout's `%s | Payna` template for this page.

     Exists for /about and nothing else so far. "About" alone is a fine
     document title and a poor sitelink anchor: it names no company, and
     the two competitors on this query both spend that slot on a branded
     one ("About Brico", "Meet the Founders"). But `title: 'About Payna'`
     through the template renders "About Payna | Payna", and Google's
     title-link guidance names repeated boilerplate as a reason it
     rewrites a title, with the replacement drawn from on-page text. On
     /about that would be the H1, "Every financial product starts with
     permission." — worse than either.

     So the brand goes in once, deliberately, and the template steps
     aside. Do not reach for this to make a title longer; the width
     budget above still applies, and "About Payna" is 114.6px. */
  brandedTitle?: boolean
}) {
  const fullTitle = brandedTitle ? title : `${title} | ${SITE_NAME}`
  return {
    /* `absolute` bypasses the root template; see brandedTitle above. */
    title: brandedTitle ? { absolute: title } : title,
    description,
    alternates: { canonical: path },
    openGraph: {
      type: 'website' as const,
      siteName: SITE_NAME,
      locale: 'en_US',
      title: fullTitle,
      description,
      url: path,
      images: [OG_IMAGE],
    },
    twitter: {
      card: 'summary_large_image' as const,
      title: fullTitle,
      description,
      images: [OG_IMAGE.url],
    },
  }
}

/* Same contract as pageMetadata, but emits og:type=article plus the
   published/modified timestamps. Separate from pageMetadata rather
   than an extra flag on it so the common case stays a three-key
   call, and so a product page can never accidentally claim to be an
   article. `noindex` exists for drafts, which are reachable by URL
   in development only. */
export function articleMetadata({
  title,
  description,
  path,
  publishedTime,
  modifiedTime,
  noindex = false,
}: {
  title: string
  description: string
  path: string
  publishedTime: string
  modifiedTime?: string
  noindex?: boolean
}) {
  const fullTitle = `${title} | ${SITE_NAME}`
  return {
    title,
    description,
    alternates: { canonical: path },
    ...(noindex ? { robots: { index: false, follow: false } } : {}),
    openGraph: {
      type: 'article' as const,
      siteName: SITE_NAME,
      locale: 'en_US',
      title: fullTitle,
      description,
      url: path,
      images: [OG_IMAGE],
      publishedTime,
      modifiedTime: modifiedTime ?? publishedTime,
      authors: [SITE_URL],
    },
    twitter: {
      card: 'summary_large_image' as const,
      title: fullTitle,
      description,
      images: [OG_IMAGE.url],
    },
  }
}
